<template>
  <PublicLayout
    variant="no-bg"
    :breadcrumbs="breadcrumbs"
    :hero="false"
    breadcrumbs-in-content
    :ui="{
      main: 'pt-0!',
      contentBreadcrumbs: 'mb-3!',
    }"
  >
    <UContainer>
      <PageContentSlider
        class="mb-10"
        title="Найти новость"
        description="Выберите интересующие вас параметры, чтобы найти подходящую новость"
        :counter="{
          amount: `100`,
          text: 'Новостей на портале',
        }"
        location-badge
        image="/images/schools/megafon.webp"
        :slides="newsWithLinks"
        to-prefix="/news"
        :loading="pending"
        :ui="{
          contentCard: 'max-lg:max-w-full',
          image:
            'transform-none! h-64! max-sm:h-48.75! md:-right-8! md:-bottom-12! max-sm:-top-5 max-sm:left-none max-sm:-right-25!',
          counterText: 'w-4/7',
          description:
            'max-sm:w-7/9! max-md:mb-6 max-lg:w-5/8! line-clamp-none',
          featuredCardInfo: 'max-sm:mb-0!',
          featuredCardTitle: 'mb-2!',
          counter: 'max-lg:hidden!',
        }"
      >
        <template #card-footer>
          <PageSearchFilter
            v-model:directions="selectedDirections"
            v-model:query="query"
            class="rounded-none! lg:hidden shadow-none!"
            placeholder="Поиск по новостям"
            :ui="{
              body: 'p-0!',
            }"
            @search="search"
          />
        </template>

        <template #slide-badges="{ slide }">
          <UBadge
            class="rounded-[250px] text-white! ring-white bg-transparent"
            variant="subtle"
            icon="i-local-calendar"
            :ui="{
              leadingIcon: 'text-white',
            }"
          >
            {{ dayjs(slide.published_at).format('DD MMMM YYYY') }}
          </UBadge>
        </template>

        <template #slide-button="{ slide }">
          <UButton
            class="mb-10.5 max-lg:w-max text-button-secondary-foreground max-sm:hidden"
            trailing-icon="i-local-move-up-right"
            color="white"
            :to="'/news/' + slide.id"
            :ui="{
              trailingIcon: 'text-button-secondary-foreground',
            }"
          >
            Подробнее
          </UButton>
        </template>
      </PageContentSlider>

      <PageSearchFilter
        v-model:directions="selectedDirections"
        v-model:query="query"
        class="mb-10 max-lg:hidden"
        placeholder="Поиск по новостям"
        @search="search"
      />

      <ExpandableList v-slot="{ displayItems }" :items="news" :limit="9">
        <CommonGridCards :items="displayItems" :pending="pending">
          <template #card="{ item }">
            <CommonCard
              :item="item"
              :to="`/news/${item.id}`"
              image-top-left="lesson-types"
              image-top-right="rating"
              title-above-left="school"
              :description="item.preview_description"
              description-under-left="date"
              description-under-right="directions"
              :ui="{
                title: 'line-clamp-2!',
              }"
            />
          </template>
        </CommonGridCards>
      </ExpandableList>
    </UContainer>
  </PublicLayout>
</template>

<script setup lang="ts">
import { dayjs } from '~/shared/day'
import PublicLayout from '~/layouts/PublicLayout.vue'
import ExpandableList from '~/components/ExpandableList.vue'
import type { BreadcrumbItem, DirectionMenuItem, SearchParams } from '~/types'

defineOptions({
  name: 'NewsPage',
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    name: 'Главная',
    to: '/',
  },
  {
    name: 'Новости',
  },
]

definePageMeta({
  layout: false,
  breadcrumbs,
})

const route = useRoute()
const directoryStore = useDirectoryStore()
const { news } = storeToRefs(directoryStore)

const { untilReady } = useRegionReady()

const { pending } = await useAppAsyncData(`news-${route.path}`, async () => {
  await untilReady()
  await Promise.all([
    directoryStore.fetchDirections(),
    directoryStore.fetchLessonTypes(),
    directoryStore.fetchNews(),
  ])

  return true
})

const selectedDirections = ref<DirectionMenuItem[]>([])
const query = ref('')

const newsWithLinks = computed(() =>
  news.value
    .map((item) => ({
      ...item,
      links: [
        {
          icon: 'i-local-graduation-cap',
          text: item.source?.name,
          color: 'bg-accent-default',
        },
        {
          icon: 'i-local-location',
          text: item.region?.name ?? '',
          color: 'bg-add-light',
        },
      ],
    }))
    .map((item) => {
      delete (item as any).preview_description
      return item
    }),
)

const search = async (query?: SearchParams) => {
  await directoryStore.fetchNews(query)
}
</script>

<style lang="scss" scoped>
:deep(.common-card) {
  --card-height: 470px;
  --card-image-height: 240px;

  @include max-xl {
    --card-height: 385px;
    --card-image-height: 180px;
  }

  @include max-md {
    --card-height: 345px;
    --card-image-height: 160px;
  }

  :deep(.body-header) {
    @include max-sm {
      display: none;
    }
  }
}

:deep(.link-badge) {
  max-width: 50%;
  min-width: unset;

  .link-text {
    font-size: 14px !important;
  }
}

:deep(.badge-icon) {
  width: 24px;
  height: 24px;
}

:deep(.description) {
  height: unset !important;
}

:deep(.direction-icon) {
  width: 24px;
  height: 24px;
}

:deep(.page-content-slider) {
  .image {
    @include isLessThan('xl') {
      width: 204px;
      left: 40%;
      z-index: 0;
    }

    @include isLessThan('lg') {
      width: 195px;
      top: 10px;
      left: 70%;
      z-index: 0;
    }
  }
}

:deep(.page-search-filter) {
  @include isLessThan(lg) {
    position: relative;
    z-index: 1;
  }
}

:deep(.links) {
  flex-wrap: nowrap;
}

:deep(.link-badge) {
  flex: 0 1 auto;
  width: unset;
  max-width: unset;

  &:first-child {
    flex: 1 1 60%;

    @include isMobile {
      display: none;
    }
  }
}
</style>
